import maplibregl from 'maplibre-gl';
import BaseProduct from './BaseProduct';
import LayerManager from './LayerManager';
import { ProductId } from '../types';
import store from '../state';
import { ManifestTimelineHelper } from '../services/ManifestTimelineHelper';
import { buildPmtilesUrl, cleanUrl } from '../config';

export class SSTLayer extends BaseProduct {
  public readonly id: ProductId = 'sst';
  private hoverPopup: maplibregl.Popup | null = null;
  private currentModel: string = 'noaa_crw_5km';
  private buoysEnabled: boolean = true;
  private interactionsBound: boolean = false;

  constructor(map: maplibregl.Map, layerManager: LayerManager) {
    super(map, layerManager);

    store.subscribe((state) => {
      const activeModel = state.selectedModels?.['sst'] || 'noaa_crw_5km';
      const isSstActive = state.sstEnabled || state.activeLayerProducts?.['sst'] || false;

      if (this.currentModel !== activeModel) {
        this.currentModel = activeModel;
        if (this.active) {
          this.updateRasterTile();
        }
      }

      if (this.buoysEnabled !== state.sstBuoysEnabled) {
        this.buoysEnabled = state.sstBuoysEnabled;
        this.setBuoyVisibility(this.buoysEnabled);
      }

      if (isSstActive !== this.active) {
        if (isSstActive) {
          this.activate();
        } else {
          this.deactivate();
        }
      }
    });
  }

  protected async onActivate(): Promise<void> {
    await this.initBuoySourceAndLayer();
    this.updateRasterTile();
  }

  protected onDeactivate(): void {
    this.layerManager.removeLayer(this.id);
    this.setBuoyVisibility(false);
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
  }

  protected onTimeUpdate(dateStr: string, hourStr: string): void {
    if (!this.active) return;
    this.updateRasterTile(dateStr, hourStr);
  }

  private updateRasterTile(targetDateStr?: string, targetHourStr?: string): void {
    const manifest = store.getState().manifest;
    if (!manifest) return;

    const opacity = 0.85;

    let dateStr = targetDateStr;
    let hourStr = targetHourStr;

    if (!dateStr || !hourStr) {
      const state = store.getState();
      const frame = ManifestTimelineHelper.getClampedFrame(
        manifest,
        this.id,
        this.currentModel,
        state.currentTimeStep || new Date()
      );
      dateStr = frame?.dateStr || new Date().toISOString().split('T')[0];
      hourStr = frame?.hourStr || 'latest';
    }

    const tileUrl = this.getTileUrl(manifest, this.id, this.currentModel, dateStr, hourStr);
    this.layerManager.updateRasterLayer(this.id, [tileUrl], opacity, undefined, 0, 5);
  }

  private getTileUrl(manifest: any, _product: string, model: string, _dateStr: string, _hourStr: string): string {
    const apiServer = manifest?.api_server || '';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');

    let pmtilesFileName = 'sst_latest.pmtiles';
    if (model && model !== 'default' && model !== 'noaa_crw_5km') {
      pmtilesFileName = `sst_${model}_latest.pmtiles`;
    }

    let cleanPath = `${tileRoot}/observed/sst/${pmtilesFileName}`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    const rawPmtiles = apiServer ? `${apiServer}/${cleanPath}` : cleanPath;
    return buildPmtilesUrl(rawPmtiles);
  }

  private async initBuoySourceAndLayer(): Promise<void> {
    if (!this.map.getSource('ndbc-buoys-source')) {
      const manifest = store.getState().manifest;
      const apiServer = manifest?.api_server || '';
      const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');

      let cleanPath = `${tileRoot}/observed/sst/ndbc_buoys.json`.replace(/\/+/g, '/');
      if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
      }
      const url = cleanUrl(apiServer ? `${apiServer}/${cleanPath}` : cleanPath);

      this.map.addSource('ndbc-buoys-source', {
        type: 'geojson',
        data: url,
        cluster: false
      });
    }

    if (!this.map.getLayer('ndbc-buoys-circles')) {
      this.map.addLayer({
        id: 'ndbc-buoys-circles',
        type: 'circle',
        source: 'ndbc-buoys-source',
        paint: {
          'circle-radius': [
            'interpolate',
            ['linear'],
            ['zoom'],
            3, 4,
            8, 8
          ],
          'circle-color': [
            'step',
            ['coalesce', ['get', 'water_temp_f'], 55],
            '#1e3a8a', // < 45°F
            45, '#06b6d4', // 45-55°F
            55, '#10b981', // 55-65°F
            65, '#f59e0b', // 65-75°F
            75, '#ef4444'  // > 75°F
          ] as any,
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 1.5,
          'circle-opacity': 0.95
        }
      });
    }

    if (!this.interactionsBound) {
      this.setupInteractions();
      this.interactionsBound = true;
    }

    this.setBuoyVisibility(store.getState().sstBuoysEnabled);
  }

  private setBuoyVisibility(visible: boolean): void {
    const val = visible ? 'visible' : 'none';
    if (this.map.getLayer('ndbc-buoys-circles')) {
      this.map.setLayoutProperty('ndbc-buoys-circles', 'visibility', val);
    }
  }

  private setupInteractions(): void {
    this.hoverPopup = new maplibregl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 10,
      className: 'ndbc-buoy-hover-popup'
    });

    this.map.on('mouseenter', 'ndbc-buoys-circles', (e) => {
      this.map.getCanvas().style.cursor = 'pointer';
      if (!e.features || e.features.length === 0) return;

      const f = e.features[0];
      const props = f.properties as any;
      const coords = (f.geometry as any).coordinates.slice();

      const wtmpText = props.water_temp_f !== null && props.water_temp_f !== undefined ? `${props.water_temp_f}°F (${props.water_temp_c}°C)` : 'N/A';
      const atmpText = props.air_temp_f !== null && props.air_temp_f !== undefined ? `${props.air_temp_f}°F (${props.air_temp_c}°C)` : 'N/A';
      const waveText = props.wave_height_ft !== null && props.wave_height_ft !== undefined ? `${props.wave_height_ft} ft (${props.wave_height_m} m)` : 'N/A';
      const periodText = props.wave_period_sec !== null && props.wave_period_sec !== undefined ? `${props.wave_period_sec} s` : 'N/A';
      const windText = props.wind_speed_kts !== null && props.wind_speed_kts !== undefined ? `${props.wind_speed_kts} kts` : 'N/A';
      const presText = props.pressure_hpa !== null && props.pressure_hpa !== undefined ? `${props.pressure_hpa} hPa` : 'N/A';

      const html = `
        <div style="font-family: sans-serif; padding: 8px 10px; color: #fff; background: rgba(15, 23, 42, 0.96); border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.5); min-width: 230px; border: 1px solid rgba(255,255,255,0.1);">
          <div style="font-weight: 700; font-size: 13px; color: #38BDF8; margin-bottom: 2px;">🌊 ${props.name}</div>
          <div style="font-size: 11px; color: #9CA3AF; margin-bottom: 6px;">NOAA NDBC Buoy: ${props.id}</div>
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 11px;">
            <div><strong>Water Temp:</strong> <span style="color: #34D399;">${wtmpText}</span></div>
            <div><strong>Air Temp:</strong> <span style="color: #FBBF24;">${atmpText}</span></div>
            <div><strong>Wave Height:</strong> <span style="color: #60A5FA;">${waveText}</span></div>
            <div><strong>Swell Period:</strong> <span style="color: #A7F3D0;">${periodText}</span></div>
            <div><strong>Wind Speed:</strong> <span style="color: #F472B6;">${windText}</span></div>
            <div><strong>Pressure:</strong> <span style="color: #E2E8F0;">${presText}</span></div>
          </div>
        </div>
      `;

      this.hoverPopup!.setLngLat(coords).setHTML(html).addTo(this.map);
    });

    this.map.on('mouseleave', 'ndbc-buoys-circles', () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup) {
        this.hoverPopup.remove();
      }
    });
  }
}

export default SSTLayer;
